import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Rating } from '@mui/material';
import {
  propertyApi,
  type PropertyData,
  PropertyQualityLabels,
} from '../services';
import { useToast } from '../contexts/ToastContext';
import PropertyEvaluationChart from '../components/PropertyEvaluationChart';
import PropertyFiles from '../components/PropertyFiles';
import './PropertyPage.css';
import TrashIcon from '../assets/trash.svg';
import GraphIcon from '../assets/graph.svg';
import EuroIcon from '../assets/euro.svg';

const DevConditionClassificationScore: React.FC<{
  gewType?: string;
  features: any;
  ratings: any;
}> = ({ gewType, features, ratings }) => {
  const [config, setConfig] = useState<any>(null);

  useEffect(() => {
    if (gewType) {
      propertyApi
        .getConditionClassificationConfig(gewType)
        .then(setConfig)
        .catch(console.error);
    }
  }, [gewType]);

  if (!config) return null;

  const calculateConditionClassificationDynamic = (
    features: any,
    ratings: any,
    config: any,
  ) => {
    let actualScore = 0;
    let maximumScore = 0;

    const getRawValue = (val: any) => {
      if (val && typeof val === 'object' && 'value' in val) {
        return val.value;
      }
      return val;
    };

    if (features && config.weights?.features) {
      for (const item of config.weights.features) {
        const featureObj = features[item.id];
        if (featureObj !== undefined && featureObj !== null) {
          const featureValue = getRawValue(featureObj);
          const val = featureValue ? 1 : 0;
          actualScore += val * item.value;
          maximumScore += 1 * item.value;
        }
      }
    }

    if (ratings && config.weights?.ratings) {
      for (const item of config.weights.ratings) {
        const ratingObj = ratings[item.id];
        if (ratingObj !== undefined && ratingObj !== null) {
          const ratingValue = getRawValue(ratingObj);
          const val = Number(ratingValue);
          if (!isNaN(val)) {
            actualScore += val * item.value;
            maximumScore += 5 * item.value;
          }
        }
      }
    }

    const percentageScore =
      maximumScore > 0 ? (actualScore / maximumScore) * 100 : 0;
    return {
      actualScore,
      maximumScore,
      percentageScore,
      thresholds: config.thresholds,
    };
  };

  const { actualScore, maximumScore, percentageScore, thresholds } =
    calculateConditionClassificationDynamic(features, ratings, config);

  return (
    <div
      style={{
        marginTop: '20px',
        padding: '15px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        backgroundColor: 'rgba(20, 20, 30, 0.4)',
        backdropFilter: 'blur(10px)',
        fontFamily: 'monospace',
        color: '#fff',
      }}
    >
      <h3
        style={{
          marginTop: 0,
          fontSize: '14px',
          color: '#aaa',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        Dev: Condition Classification Score
      </h3>
      <div style={{ marginBottom: '15px', fontSize: '13px' }}>
        <strong>Score:</strong> {actualScore} / {maximumScore} (
        {percentageScore.toFixed(2)}%)
        <br />
        <span style={{ opacity: 0.7, fontSize: '11px' }}>
          Bounds: Mäßig &lt; {thresholds.low}% | Gut &lt; {thresholds.high}% |
          Sehr Gut &ge; {thresholds.high}%
        </span>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '8px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            width: `${thresholds.low}%`,
            height: '100%',
            backgroundColor: '#f44336',
            opacity: 0.6,
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            left: `${thresholds.low}%`,
            width: `${thresholds.high - thresholds.low}%`,
            height: '100%',
            backgroundColor: '#ffeb3b',
            opacity: 0.6,
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            left: `${thresholds.high}%`,
            width: `${100 - thresholds.high}%`,
            height: '100%',
            backgroundColor: '#4caf50',
            opacity: 0.6,
          }}
        ></div>

        {/* Indicator */}
        <div
          style={{
            position: 'absolute',
            left: `calc(${Math.min(Math.max(percentageScore, 0), 100)}% - 6px)`,
            top: '-2px',
            width: '12px',
            height: '12px',
            backgroundColor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 0 5px rgba(0,0,0,0.5)',
            zIndex: 2,
          }}
        ></div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          marginTop: '8px',
          color: '#aaa',
        }}
      >
        <span style={{ width: `${thresholds.low}%`, textAlign: 'center' }}>
          mäßig
        </span>
        <span
          style={{
            width: `${thresholds.high - thresholds.low}%`,
            textAlign: 'center',
          }}
        >
          gut
        </span>
        <span
          style={{ width: `${100 - thresholds.high}%`, textAlign: 'center' }}
        >
          sehr gut
        </span>
      </div>
    </div>
  );
};

const PropertyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  type TimePeriod = '1month' | '6months' | '12months' | '5years' | 'all';
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');

  const emptyProperty: PropertyData = {
    id: '',
    address: '',
    country: 'Österreich',
    gain: 0,
    gainPercentage: 0,
    features: {},
    ratings: {},
  };

  const [property, setProperty] = useState<PropertyData>(emptyProperty);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showSellWizard, setShowSellWizard] = useState<boolean>(false);
  const [sellAmount, setSellAmount] = useState<string>('');
  const [sellDate, setSellDate] = useState<string>('');
  const [showBuyModal, setShowBuyModal] = useState<boolean>(false);
  const [buyPrice, setBuyPrice] = useState<string>('');
  const [buyDate, setBuyDate] = useState<string>('');
  const evaluationRefreshTrigger = 0;
  const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false);

  // Refs for map
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerInstanceRef = useRef<google.maps.Marker | null>(null);

  // Function to update map location
  const updateMapLocation = (lat: number, lng: number) => {
    if (mapInstanceRef.current) {
      const position = { lat, lng };
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(15);

      const googleMaps = (window as any).google;
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setPosition(position);
      } else if (googleMaps && googleMaps.maps) {
        markerInstanceRef.current = new googleMaps.maps.Marker({
          position,
          map: mapInstanceRef.current,
        });
      }
    }
  };

  // Function to initialize map (can be called multiple times safely)
  const initializeMap = () => {
    if (!mapRef.current) {
      return false;
    }

    // Check if element is actually in the DOM
    if (!document.contains(mapRef.current)) {
      return false;
    }

    // Ensure the container has dimensions - Google Maps requires explicit dimensions
    const rect = mapRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Map) {
      return false;
    }

    if (!mapInstanceRef.current) {
      try {
        // Check if Google Maps already initialized this container
        const hasGoogleMapsContent =
          mapRef.current.querySelector('[class*="gm-"]') ||
          mapRef.current.querySelector('div[style*="overflow"]');

        if (!hasGoogleMapsContent) {
          // Clear only if no Google Maps content exists
          mapRef.current.innerHTML = '';
        }

        mapInstanceRef.current = new googleMaps.maps.Map(mapRef.current, {
          center: { lat: 47.5162, lng: 14.5501 }, // Default to Austria
          zoom: 7,
          disableDefaultUI: true,
        });

        // Trigger resize after a short delay to ensure map renders correctly
        setTimeout(() => {
          if (mapInstanceRef.current && mapRef.current) {
            const googleMaps = (window as any).google;
            if (googleMaps && googleMaps.maps && googleMaps.maps.event) {
              googleMaps.maps.event.trigger(mapInstanceRef.current, 'resize');

              // Re-initialize if container was cleared after resize
              if (mapRef.current.children.length === 0) {
                mapInstanceRef.current = null;
                setIsMapInitialized(false);
                initializeMap();
              }
            }
          }
        }, 200);

        setIsMapInitialized(true);
        return true;
      } catch (error) {
        return false;
      }
    }

    return true;
  };

  // Initialize map - retry until Google Maps is loaded
  // Also re-initialize when property ID changes (navigate to different property)
  useEffect(() => {
    // Store the current ID to prevent race conditions
    const currentId = id;
    let isMounted = true;

    // Reset map instance and state when property ID changes
    // mapInstanceRef.current = null; // Don't reset map instance to avoid flickering/reloading if possible, but for ID change we might need to repointer
    // Actually for view page, ID won't change often without full navig logic
    // But let's keep original logic for safety
    mapInstanceRef.current = null;
    markerInstanceRef.current = null;
    setIsMapInitialized(false);

    // Small delay to ensure DOM is ready and avoid React StrictMode double-initialization issues
    const initTimeout = setTimeout(() => {
      if (!isMounted || currentId !== id) return;

      // Try to initialize immediately
      if (initializeMap()) {
        return; // Map initialized successfully
      }

      // If not initialized, set up a retry mechanism
      let retryCount = 0;
      const maxRetries = 100; // 10 seconds max wait time (100 * 100ms)

      const retryInterval = setInterval(() => {
        if (!isMounted || currentId !== id) {
          clearInterval(retryInterval);
          return;
        }

        retryCount++;
        if (initializeMap()) {
          clearInterval(retryInterval);
        } else if (retryCount >= maxRetries) {
          clearInterval(retryInterval);
        }
      }, 100);

      return () => clearInterval(retryInterval);
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      markerInstanceRef.current = null;
    };
  }, [id]); // Re-run when property ID changes

  // Update map location when coordinates are available (e.g., after save or when property loads)
  useEffect(() => {
    if (
      property.latitude !== undefined &&
      property.longitude !== undefined &&
      property.latitude !== null &&
      property.longitude !== null
    ) {
      // Wait for map to be initialized before updating
      if (isMapInitialized && mapInstanceRef.current) {
        // Map is ready, update location immediately
        updateMapLocation(property.latitude, property.longitude);
      } else {
        // Map not ready yet, wait a bit and try again
        let retryCount = 0;
        const maxRetries = 100;

        const tryUpdate = () => {
          if (isMapInitialized && mapInstanceRef.current) {
            updateMapLocation(property.latitude!, property.longitude!);
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(tryUpdate, 100);
          }
        };

        setTimeout(tryUpdate, 100);
      }
    }
  }, [property.latitude, property.longitude, isMapInitialized]);

  // Helper function to convert property from API format to display format
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    // Remove any fields that shouldn't be in PropertyData (like userId, user)
    const { userId: _, user: __, ...cleanProp } = prop as any;

    return {
      ...cleanProp,
      kaufdatum: cleanProp.kaufdatum
        ? typeof cleanProp.kaufdatum === 'string'
          ? cleanProp.kaufdatum.split('T')[0]
          : new Date(cleanProp.kaufdatum).toISOString().split('T')[0]
        : undefined,
      // Convert string numbers from backend to actual numbers
      kaufpreis:
        cleanProp.kaufpreis !== undefined && cleanProp.kaufpreis !== null
          ? typeof cleanProp.kaufpreis === 'string'
            ? parseFloat(cleanProp.kaufpreis)
            : cleanProp.kaufpreis
          : undefined,
      currentValue:
        cleanProp.currentValue !== undefined && cleanProp.currentValue !== null
          ? typeof cleanProp.currentValue === 'string'
            ? parseFloat(cleanProp.currentValue)
            : cleanProp.currentValue
          : undefined,
      gain:
        typeof cleanProp.gain === 'string'
          ? parseFloat(cleanProp.gain)
          : cleanProp.gain,
      gainPercentage:
        typeof cleanProp.gainPercentage === 'string'
          ? parseFloat(cleanProp.gainPercentage)
          : cleanProp.gainPercentage,
      // Convert latitude/longitude from string to number if needed
      latitude:
        cleanProp.latitude !== undefined && cleanProp.latitude !== null
          ? typeof cleanProp.latitude === 'string'
            ? parseFloat(cleanProp.latitude)
            : cleanProp.latitude
          : undefined,
      longitude:
        cleanProp.longitude !== undefined && cleanProp.longitude !== null
          ? typeof cleanProp.longitude === 'string'
            ? parseFloat(cleanProp.longitude)
            : cleanProp.longitude
          : undefined,
      sellDate: cleanProp.sellDate
        ? typeof cleanProp.sellDate === 'string'
          ? cleanProp.sellDate.split('T')[0]
          : new Date(cleanProp.sellDate).toISOString().split('T')[0]
        : undefined,
      sellAmount:
        cleanProp.sellAmount !== undefined && cleanProp.sellAmount !== null
          ? typeof cleanProp.sellAmount === 'string'
            ? parseFloat(cleanProp.sellAmount)
            : cleanProp.sellAmount
          : undefined,
      // Always set country to Österreich if not set
      country: cleanProp.country || 'Österreich',
      features: cleanProp.features || {},
      ratings: cleanProp.ratings || {},
    };
  };

  useEffect(() => {
    const fetchProperty = async () => {
      if (id) {
        setLoading(true);
        try {
          const propertyData = await propertyApi.getById(id);
          const normalized = normalizeProperty(propertyData);
          setProperty(normalized);
        } catch (error: any) {
          showError(error.message || 'Failed to load property');
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProperty();
  }, [id]);



  const handleDeleteProperty = async () => {
    if (!id) return;
    try {
      await propertyApi.delete(id);
      showSuccess('Immobilie erfolgreich entfernt');
      navigate('/dashboard');
    } catch (error: any) {
      showError(error.message || 'Fehler beim Entfernen der Immobilie');
    }
  };

  const handleSellProperty = async () => {
    if (!id || !sellAmount || !sellDate) return;
    try {
      setLoading(true);
      const updated = await propertyApi.sell(id, {
        sellAmount: parseFloat(sellAmount),
        sellDate,
      });
      setProperty(normalizeProperty(updated));
      setShowSellWizard(false);
      showSuccess('Immobilie erfolgreich als verkauft markiert');
    } catch (error: any) {
      showError(error.message || 'Fehler beim Speichern der Verkaufsdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyProperty = async () => {
    if (!id || !buyPrice || !buyDate) return;
    try {
      setLoading(true);
      const updated = await propertyApi.buy(id, {
        kaufpreis: parseFloat(buyPrice),
        kaufdatum: buyDate,
      });
      setProperty(normalizeProperty(updated));
      setShowBuyModal(false);
      showSuccess('Immobilie erfolgreich als gekauft markiert');
    } catch (error: any) {
      showError(error.message || 'Fehler beim Speichern der Kaufdaten');
    } finally {
      setLoading(false);
    }
  };

  const renderEditableField = (
    _fieldName: string,
    _label: string,
    value: string | number | boolean | undefined,
    type:
      | 'text'
      | 'number'
      | 'date'
      | 'select'
      | 'textarea'
      | 'checkbox' = 'text',
    _options?: string[],
  ) => {
    if (type === 'checkbox') {
      const boolValue = value === true || value === 'true' || value === 1;
      return <div className="read-only-field">{boolValue ? 'Yes' : 'No'}</div>;
    }

    let displayValue = value;
    if (value === undefined || value === null || value === '') {
      return <div className="read-only-field">-</div>;
    }

    if (type === 'date') {
      const parts = String(value).split('-');
      displayValue =
        parts.length === 3
          ? `${parts[2]}.${parts[1]}.${parts[0]}`
          : new Date(String(value)).toLocaleDateString();
    } else if (type === 'number') {
      // Optional: Format numbers if needed
    }

    return <div className="read-only-field">{String(displayValue)}</div>;
  };

  if (loading) {
    return (
      <div className="property-page">
        <div className="property-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Loading property...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="property-page">
      <div className="property-container">
        <header className="property-header">
          <button
            className="back-button"
            onClick={() => navigate('/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </header>

        <div className="property-action-row">
          {!property.sellDate && (!property.kaufpreis || !property.kaufdatum) && (
            <button
              className="action-button primary"
              onClick={() => {
                setBuyPrice('');
                setBuyDate(new Date().toISOString().split('T')[0]);
                setShowBuyModal(true);
              }}
              style={{ marginRight: '12px' }}
            >
              <img src={EuroIcon} alt="" />
              Immobilie kaufen
            </button>
          )}
          {!property.sellDate && property.kaufpreis && property.kaufdatum && (
            <button
              className="action-button primary"
              onClick={() => {
                setSellAmount('');
                setSellDate(new Date().toISOString().split('T')[0]);
                setShowSellWizard(true);
              }}
              style={{ marginRight: '12px' }}
            >
              <img src={EuroIcon} alt="" />
              Immobilie verkaufen
            </button>
          )}
          <button
            className="action-button secondary"
            onClick={() => setShowDeleteModal(true)}
          >
            <img src={TrashIcon} alt="" />
            Aus Portfolio entfernen
          </button>
        </div>

        <div className="property-content">
          <h1 className="property-title">
            {[
              [property.streetName, property.houseNumber]
                .filter(Boolean)
                .join(' '),
              property.city,
              property.country,
            ]
              .filter(Boolean)
              .join(' • ') ||
              property.address ||
              'Property Details'}
            {property.sellDate && (
              <span className="sold-badge" style={{
                marginLeft: '16px',
                padding: '4px 12px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#f13050',
                borderRadius: '8px',
                textTransform: 'uppercase',
                verticalAlign: 'middle',
                display: 'inline-block'
              }}>
                Verkauft
              </span>
            )}
          </h1>

          <div className="property-layout">
            {/* Left Column */}
            <div className="property-left-column">
              {/* Map Section */}
              <div className="map-section">
                <div
                  ref={mapRef}
                  className="map-container"
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                  }}
                />
              </div>

              {/* General Data Section */}
              <div className="detail-section">
                <h2>General Data</h2>
                <div className="detail-list">
                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>GewType</label>
                      <div className="read-only-field">
                        {property.gewType ? String(property.gewType) : '-'}
                      </div>
                    </div>
                    <div className="detail-item">
                      <label>Quality</label>
                      <div className="read-only-field">
                        {property.quality
                          ? PropertyQualityLabels[
                              property.quality as keyof typeof PropertyQualityLabels
                            ]
                          : '-'}
                      </div>
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Area (m²)</label>
                      {renderEditableField(
                        'area',
                        'Area',
                        property.area,
                        'number',
                      )}
                    </div>
                  </div>

                  <div className="detail-section-subtitle">
                    Ausstattung & Lage
                  </div>
                  <div className="attributes-grid">
                    {Object.entries(property.features || {}).map(
                      ([key, featObj]) => {
                        const isActive =
                          featObj &&
                          typeof featObj === 'object' &&
                          'value' in featObj
                            ? featObj.value === true
                            : featObj === true;
                        const label =
                          featObj &&
                          typeof featObj === 'object' &&
                          'localization' in featObj
                            ? featObj.localization
                            : key;
                        return (
                          <div key={key} className="attribute-item">
                            <div
                              className={`attribute-icon ${isActive ? 'active' : 'inactive'}`}
                            >
                              {isActive ? '✓' : '✗'}
                            </div>
                            <div className="attribute-label">{label}</div>
                          </div>
                        );
                      },
                    )}
                  </div>

                  <div className="detail-section-subtitle">
                    Zustand & Qualität
                  </div>
                  <div
                    className="detail-item-row"
                    style={{
                      flexDirection: 'column',
                      gap: '1rem',
                      alignItems: 'stretch',
                    }}
                  >
                    {Object.entries(property.ratings || {}).map(
                      ([key, ratingObj]) => {
                        const val =
                          ratingObj &&
                          typeof ratingObj === 'object' &&
                          'value' in ratingObj
                            ? ratingObj.value
                            : Number(ratingObj) || 1;
                        const label =
                          ratingObj &&
                          typeof ratingObj === 'object' &&
                          'localization' in ratingObj
                            ? ratingObj.localization
                            : key;
                        return (
                          <div
                            key={key}
                            className="detail-item"
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <label style={{ margin: 0 }}>{label}</label>
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <Rating value={val} precision={0.5} readOnly />
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>

                  {/* Address Fields */}
                  <div className="detail-item-row">
                    <div
                      className="detail-item"
                      style={{
                        position: 'relative',
                        width: '80%',
                        flex: '1 1 80%',
                      }}
                    >
                      <label>Street Name</label>
                      {renderEditableField(
                        'streetName',
                        'Street Name',
                        property.streetName,
                        'text',
                      )}
                    </div>
                    <div
                      className="detail-item"
                      style={{
                        position: 'relative',
                        width: '20%',
                        flex: '0 0 20%',
                      }}
                    >
                      <label>Number</label>
                      {renderEditableField(
                        'houseNumber',
                        'House Number',
                        property.houseNumber,
                        'text',
                      )}
                    </div>
                  </div>
                  <div className="detail-item-row">
                    <div
                      className="detail-item"
                      style={{ position: 'relative' }}
                    >
                      <label>City</label>
                      {renderEditableField(
                        'city',
                        'City',
                        property.city,
                        'text',
                      )}
                    </div>
                    <div
                      className="detail-item"
                      style={{ position: 'relative' }}
                    >
                      <label>Country</label>
                      {renderEditableField(
                        'country',
                        'Country',
                        property.country || 'Österreich',
                        'text',
                      )}
                    </div>
                  </div>

                  {/* Conditional fields based on property type */}
                </div>
              </div>

              {/* Financial Data Section */}
              <div className="detail-section">
                <h2>Financial Data</h2>
                <div className="detail-list">
                  <div className="detail-item">
                    <label>Purchase Price</label>
                    <div className="detail-value">
                      <span className="read-only-field">
                        {property.kaufpreis !== undefined
                          ? `€${property.kaufpreis.toLocaleString()}`
                          : '-'}
                      </span>
                    </div>
                  </div>{' '}
                  <div className="detail-item">
                    <label>Purchase Date</label>
                    <div className="detail-value">
                      {renderEditableField(
                        'kaufdatum',
                        'Purchase Date',
                        property.kaufdatum,
                        'date',
                      )}
                    </div>
                  </div>
                  {property.sellDate && (
                    <>
                      <div className="detail-item">
                        <label>Verkaufspreis</label>
                        <div className="detail-value">
                          <span className="read-only-field">
                            {property.sellAmount !== undefined
                              ? `€${property.sellAmount.toLocaleString()}`
                              : '-'}
                          </span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <label>Verkaufsdatum</label>
                        <div className="detail-value">
                          {renderEditableField(
                            'sellDate',
                            'Verkaufsdatum',
                            property.sellDate,
                            'date',
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="detail-item">
                    <label>Total Gain</label>
                    {property.kaufpreis !== undefined && property.kaufpreis !== null ? (
                      <div
                        className={`detail-value ${property.gain >= 0 ? 'stat-positive' : 'stat-negative'}`}
                      >
                        €{property.gain.toLocaleString()} (
                        {property.gainPercentage > 0 ? '+' : ''}
                        {property.gainPercentage.toFixed(1)}%)
                      </div>
                    ) : (
                      <div className="detail-value" style={{ color: 'var(--Grey-2, #a7a3e2)' }}>
                        -
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="property-right-column">
              {/* Graph Section */}
              <div className="graph-section">
                <div className="graph-header">
                  <h3>Gesamtwert & Wertentwicklung</h3>
                  <img src={GraphIcon} alt="" className="graph-header-icon" />
                </div>

                <div className="graph-values">
                  <div className="graph-value-item">
                    <label>Geschätzter Gesamtwert</label>
                    <div className="graph-value-large">
                      {property.currentValue !== undefined
                        ? property.currentValue.toLocaleString('de-DE')
                        : '0'}{' '}
                      €
                      {property.kaufpreis !== undefined && property.kaufpreis !== null && property.gainPercentage !== undefined && (
                        <span
                          className={`graph-percentage ${property.gainPercentage >= 0 ? 'positive' : 'negative'}`}
                        >
                          {property.gainPercentage >= 0 ? '▲' : '▼'}{' '}
                          {Math.abs(property.gainPercentage)
                            .toFixed(1)
                            .replace('.', ',')}{' '}
                          %
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="graph-value-item">
                    <label>Geschätzte Wertentwicklung</label>
                    {property.kaufpreis !== undefined && property.kaufpreis !== null ? (
                      <div
                        className={`graph-value-large ${property.gain >= 0 ? 'positive' : 'negative'}`}
                      >
                        {property.gain >= 0 ? '+' : ''}
                        {property.gain.toLocaleString('de-DE')} €
                      </div>
                    ) : (
                      <div className="graph-value-large" style={{ color: 'var(--Grey-2, #a7a3e2)' }}>
                        -
                      </div>
                    )}
                  </div>
                </div>

                <div className="time-scope-toggles">
                  <button
                    className={`toggle-button ${timePeriod === '1month' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('1month')}
                  >
                    1 Monate
                  </button>
                  <button
                    className={`toggle-button ${timePeriod === '6months' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('6months')}
                  >
                    6 Monate
                  </button>
                  <button
                    className={`toggle-button ${timePeriod === '12months' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('12months')}
                  >
                    1 Jahr
                  </button>
                  <button
                    className={`toggle-button ${timePeriod === 'all' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('all')}
                  >
                    Max.
                  </button>
                </div>

                <div className="graph-chart-container">
                  <PropertyEvaluationChart
                    property={property}
                    refreshTrigger={evaluationRefreshTrigger}
                    timePeriod={timePeriod}
                  />
                </div>
              </div>

              {/* Dev Condition Classification Score Component */}
              <DevConditionClassificationScore
                gewType={property.gewType}
                features={property.features}
                ratings={property.ratings}
              />

              <PropertyFiles propertyId={property.id} />
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="confirm-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Immobilie aus Portfolio entfernen</h3>
            <p>
              Möchten Sie diese Immobilie wirklich aus Ihrem Portfolio entfernen? Dieser Vorgang kann nicht rückgängig gemacht werden.
            </p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="btn-confirm"
                onClick={() => {
                  setShowDeleteModal(false);
                  handleDeleteProperty();
                }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {showSellWizard && (
        <div className="confirm-modal-overlay" onClick={() => setShowSellWizard(false)}>
          <div className="confirm-modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1.5rem' }}>Immobilie verkaufen</h3>
            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
              <div className="detail-item">
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--Grey-2, #a7a3e2)' }}>Verkaufspreis (€)</label>
                <input
                  type="number"
                  className="wizard-input"
                  placeholder="z.B. 350000"
                  value={sellAmount}
                  onChange={(e) => setSellAmount(e.target.value)}
                  min="0"
                />
              </div>

              <div className="detail-item">
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--Grey-2, #a7a3e2)' }}>Verkaufsdatum</label>
                <input
                  type="date"
                  className="wizard-input"
                  value={sellDate}
                  onChange={(e) => setSellDate(e.target.value)}
                />
              </div>
            </div>

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowSellWizard(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="action-button primary"
                style={{ padding: '12px 24px', borderRadius: '8px', fontSize: '0.95rem' }}
                onClick={handleSellProperty}
                disabled={!sellAmount || !sellDate}
              >
                Verkauf abschließen
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyModal && (
        <div className="confirm-modal-overlay" onClick={() => setShowBuyModal(false)}>
          <div className="confirm-modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1.5rem' }}>Immobilie kaufen</h3>
            
            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
              <div className="detail-item">
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--Grey-2, #a7a3e2)' }}>Kaufpreis (€)</label>
                <input
                  type="number"
                  className="wizard-input"
                  placeholder="z.B. 300000"
                  value={buyPrice}
                  onChange={(e) => setBuyPrice(e.target.value)}
                  min="0"
                />
              </div>

              <div className="detail-item">
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--Grey-2, #a7a3e2)' }}>Kaufdatum</label>
                <input
                  type="date"
                  className="wizard-input"
                  value={buyDate}
                  onChange={(e) => setBuyDate(e.target.value)}
                />
              </div>
            </div>

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowBuyModal(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="action-button primary"
                style={{ padding: '12px 24px', borderRadius: '8px', fontSize: '0.95rem' }}
                onClick={handleBuyProperty}
                disabled={!buyPrice || !buyDate}
              >
                Kauf abschließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyPage;
